// Client-side companion to `oauth-failure-diagnostics.ts`. The server callback
// proved the OAuth `pkce_code_verifier_not_found` failure is CONTEXT ISOLATION
// (the whole cookie jar — verifier AND the PostHog cookie — is absent at the
// callback; see #685). What the server CAN'T see is what kind of context the
// user actually landed in: a home-screen standalone PWA, an in-app browser
// (Gmail/SFSafariViewController), or a fresh storage partition. Those signals
// live only in the browser, and only in the broken context itself — which the
// user reaches when they get bounced to `/login?oauth_error=...`. This builds
// the probe bag captured there to PIN the mechanism the next scan reads.
//
// SAFETY: the referrer is reduced to its host (drops any `?code=` query), the
// same reduction the server diagnostics applies to the Referer header.

import { refererHost } from "./oauth-failure-diagnostics";

/** Build the client-context property bag for an OAuth code-exchange failure.
 *  Pure (takes the raw `window`/`navigator`/`document` reads as args) so it's
 *  unit-testable without a DOM. Every field answers "which isolated context
 *  is this?": `navigator_standalone` / `display_mode_standalone` flag a
 *  home-screen PWA; an empty `local_storage` + `cookie` confirm a fresh
 *  partition; `referrer_host` + `user_agent` place the originating app. */
export function oauthFailureClientContext(args: {
  errorCode: string | undefined;
  displayModeStandalone: boolean | null;
  navigatorStandalone: boolean | null;
  referrer: string | null;
  localStorageEmpty: boolean | null;
  cookieEmpty: boolean;
  userAgent: string;
}): Record<string, unknown> {
  return {
    error_code: args.errorCode ?? "unknown",
    display_mode_standalone: args.displayModeStandalone,
    navigator_standalone: args.navigatorStandalone,
    referrer_host: refererHost(args.referrer),
    local_storage_empty: args.localStorageEmpty,
    cookie_empty: args.cookieEmpty,
    user_agent: args.userAgent,
  };
}
